import {
  DBSchema,
  IDBPDatabase,
  openDB,
  StoreKey,
  StoreNames,
  StoreValue,
} from 'idb';
import { useEffect, useState } from 'react';

interface IndexDBProps<DBType extends DBSchema> {
  dbName: string;
  objectStoreName: StoreNames<DBType>;
}
const useIndexDb = <DBType extends DBSchema>({
  dbName,
  objectStoreName,
}: IndexDBProps<DBType>) => {
  const [dataBase, setDataBase] = useState<IDBPDatabase<DBType> | null>(null);

  const openDataBase = async () => {
    const db = await openDB<DBType>(dbName, 1, {
      upgrade(currentDb) {
        if (!currentDb.objectStoreNames.contains(objectStoreName)) {
          currentDb.createObjectStore(objectStoreName, {
            keyPath: 'id',
            autoIncrement: true,
          });
        }
      },
    });
    return db;
  };

  const saveDb = async () => {
    const db = await openDataBase();
    setDataBase(db);
  };

  const saveToIndexDb = async <
    ImagesType extends StoreValue<DBType, StoreNames<DBType>>,
  >({
    images,
  }: {
    images: ImagesType;
  }) => {
    if (!dataBase) return false;

    try {
      const tx = dataBase.transaction(objectStoreName, 'readwrite');
      const store = tx.objectStore(objectStoreName);
      const res = await store.add(images);
      await tx.done;
      return res;
    } catch (error) {
      return false;
    }
  };

  const getAllFromIndexDb = async () => {
    const db = await openDataBase();
    if (!db) return [];
    try {
      const tx = db.transaction(objectStoreName, 'readonly');
      const store = tx.objectStore(objectStoreName);
      const files = await store.getAll();
      await tx.done;
      return files;
    } catch (error) {
      return [];
    }
  };

  const deleteItemFromIndexDb = async (
    id: StoreKey<DBType, StoreNames<DBType>>,
  ) => {
    if (!dataBase) return false;
    try {
      const tx = dataBase.transaction(objectStoreName, 'readwrite');
      const store = tx.objectStore(objectStoreName);
      await store.delete(id);
      await tx.done;
      return true;
    } catch (error) {
      return false;
    }
  };

  const addItemToExistRecord = async ({
    id,
    updateData,
  }: {
    id: StoreKey<DBType, StoreNames<DBType>>;
    updateData: Partial<StoreValue<DBType, StoreNames<DBType>>>;
  }) => {
    if (!dataBase) return;
    const tx = dataBase.transaction(objectStoreName, 'readwrite');
    const store = tx.objectStore(objectStoreName);
    const existingRecord = await store.get(id);

    if (existingRecord) {
      await store.put({ ...existingRecord, ...updateData });
    }
    await tx.done;
  };

  useEffect(() => {
    saveDb();
  }, []);

  return {
    saveToIndexDb,
    getAllFromIndexDb,
    deleteItemFromIndexDb,
    addItemToExistRecord,
  };
};

export default useIndexDb;
